import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { msalConfig } from './AuthConfig'
import Prepare from './Screens/Prepare'
import Notice from './Components/NoticesComponent/index'
import FileUpload from './Screens/FileUpload'
import { Provider } from 'react-redux'
import { store } from './store/store'
import AppInsights from './AppInsights'
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { runtimeConfig } from './runtimeConfig'

const msalInstance = new PublicClientApplication(msalConfig)

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
  },
  {
    path: '/prepare',
    element: <Prepare />,
  },
  {
    path: '/notices',
    element: <Notice />,
  },
  {
    path: '/file-upload',
    element: <FileUpload />,
  },
])

reportWebVitals()

const environmentName = runtimeConfig.VITE_ENV;
const belongsToEU = environmentName.toLowerCase().includes("eu");
const belongsToProd = environmentName.toLowerCase().includes("prod");

async function setCookie(
  name: string,
  value: string,
  path: string,
  expires: number,
  domain?: string,
) {
  try {
    await (window as any)?.cookieStore?.set({
      name,
      value,
      domain,
      path,
      expires,
    })
    return { message: 'Success setting cookie' }
  } catch (error) {
    return { message: 'Error setting cookie' }
  }
}

function getCookie(cname: string) {
  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) == ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return null
}

const cookieScript = document.createElement('script')
cookieScript.type = 'text/javascript'
cookieScript.async = true
cookieScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'

/**
 * Setting the data-domain-script attribute based on the environment
 *
 * TODO: Once the required scripts are available for DTTL environments. I will
 * move this data-domain-script entirely to environment variable files.
 * */
if (belongsToProd) {
  if (belongsToEU) {
    console.log("Setting EU Prod Cookie Script");
    cookieScript.setAttribute('data-domain-script', runtimeConfig.VITE_COOKIE_CONSENT_DATA_ATTRIBUTE);
  } else {
    console.log("Setting US Prod Cookie Script");
    cookieScript.setAttribute('data-domain-script', runtimeConfig.VITE_COOKIE_CONSENT_DATA_ATTRIBUTE);
  }
} else {
  if (belongsToEU) {
    console.log("Setting EU Non-Prod Cookie Script");
    cookieScript.setAttribute('data-domain-script', runtimeConfig.VITE_COOKIE_CONSENT_DATA_ATTRIBUTE);
  } else {
    console.log("Setting US Non-Prod Cookie Script");
    cookieScript.setAttribute('data-domain-script', runtimeConfig.VITE_COOKIE_CONSENT_DATA_ATTRIBUTE);
  }
}

document.body.appendChild(cookieScript)

const launchScript = document.createElement('script')
launchScript.type = 'text/javascript'
launchScript.src = '//assets.adobedtm.com/571f1a0c43d6/b821caed2815/launch-0eebf70e1ab1.min.js'
document.getElementsByTagName('head')[0].appendChild(launchScript)

window.addEventListener('OneTrustGroupsUpdated', async (_event) => {
  const optOnConsentSharingValue = getCookie('OptOnConsentSharing_GenAi')
  const isAlertBox = getCookie('AlertBox')

  if (!isAlertBox && optOnConsentSharingValue) {
    const crossDomainValues = optOnConsentSharingValue.split(' | ')

    await setCookie(
      'OptanonAlertBoxClosed',
      crossDomainValues[0],
      '/',
      Number(crossDomainValues[1]),
    )
    await setCookie('OptanonConsent', crossDomainValues[2], '/', Number(crossDomainValues[3]))
  }

  else if ((window as any)?.OneTrust?.IsAlertBoxClosed()) {
    const alertBoxClosedCookie = await (window as any)?.cookieStore?.get('OptanonAlertBoxClosed')
    const optanonConsentCookie = await (window as any)?.cookieStore?.get('OptanonConsent')

    const crossDomainValue =
      alertBoxClosedCookie.value +
      ' | ' +
      alertBoxClosedCookie.expires +
      ' | ' +
      optanonConsentCookie.value +
      ' | ' +
      optanonConsentCookie.expires

    const day = 30 * 24 * 60 * 60 * 1000
    const oneMonthFromNow = Date.now() + day

    await setCookie(
      'OptOnConsentSharing_GenAi',
      crossDomainValue,

      '/',
      oneMonthFromNow,
      'deloitte.com',
    )
    await setCookie('AlertBox', 'True', '/', oneMonthFromNow)
  }
})

const rootElement = document.getElementById("root");

if (rootElement === null) {
  throw new Error("Root element not found; can't attach React!");
}

const root = ReactDOM.createRoot(rootElement);

root.render(
  <AppInsightsContext.Provider value={AppInsights.reactPlugin}>
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <RouterProvider router={router} />
      </MsalProvider>,
    </Provider>
  </AppInsightsContext.Provider>
)
